import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { ChevronDown, ChevronUp, FileUp, User, Plane, Calendar, AlertCircle, Check } from 'lucide-react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import AircraftSection from './profile/aircraft/AircraftSection';
import CertificatesSection from './profile/certificates/CertificatesSection';
import ExperienceSection from './profile/experience/ExperienceSection';
import MedicalSection from './profile/medical/MedicalSection';

const OnboardingTracker = ({ db, onComplete }) => {
  const { user } = useAuth();
  const [onboardingState, setOnboardingState] = useState({
    isComplete: false,
    certificates: false,
    medical: false,
    aircraft: false,
    experience: false,
    steps: [],
    lastUpdated: null
  });
  const [expandedSections, setExpandedSections] = useState({
    certificates: false,
    medical: false,
    aircraft: false,
    experience: false
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFirstVisit, setIsFirstVisit] = useState(true);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1); // Extracts "aircraft" from "/onboarding#aircraft"
      if (hash && document.getElementById(hash)) {
        document.getElementById(hash).scrollIntoView({ behavior: 'smooth' });
      }
    };

    // Run on mount and listen for hash changes
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);

    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    const fetchOnboardingStatus = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'pilot', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setOnboardingState(prev => ({
            ...prev,
            ...data.onboarding,
            lastUpdated: data.onboarding?.lastUpdated?.toDate()
          }));
          setIsFirstVisit(!data.onboarding?.certificates);
          
          // Auto-expand certificates section on first visit
          if (!data.onboarding?.certificates) {
            setExpandedSections(prev => ({ ...prev, certificates: true }));
          }
        } else {
          await setDoc(doc(db, 'pilot', user.uid), {
            onboarding: {
              isComplete: false,
              certificates: false,
              medical: false,
              aircraft: false,
              experience: false,
              steps: [],
              lastUpdated: new Date()
            }
          });
          setExpandedSections(prev => ({ ...prev, certificates: true }));
        }
      } catch (error) {
        setError('Failed to load onboarding status');
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchOnboardingStatus();
  }, [db, user]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const updateOnboardingStep = async (step, completed) => {
    try {
      const updates = {
        [`onboarding.${step}`]: completed,
        'onboarding.lastUpdated': new Date()
      };

      await updateDoc(doc(db, 'pilot', user.uid), updates);
      
      setOnboardingState(prev => ({
        ...prev,
        [step]: completed,
        lastUpdated: new Date()
      }));

      checkCompletion();
    } catch (error) {
      setError(`Failed to update ${step} status: ${error.message}`);
    }
  };

  const checkCompletion = () => {
    const allComplete = ['certificates', 'medical', 'aircraft', 'experience']
      .every(step => onboardingState[step]);
    
    if (allComplete && !onboardingState.isComplete) {
      updateDoc(doc(db, 'pilot', user.uid), {
        'onboarding.isComplete': true
      });
      onComplete?.();
    }
  };

  const getProgress = () => {
    const completedSteps = Object.entries(onboardingState)
      .filter(([key, value]) => key !== 'isComplete' && key !== 'lastUpdated' && key !== 'steps')
      .filter(([_, value]) => value)
      .length;
    return (completedSteps / 4) * 100;
  };

  const renderSection = (title, icon, section, children) => {
    const Icon = icon;
    const isExpanded = expandedSections[section];
    const isComplete = onboardingState[section];

    return (
      <div className="border rounded-lg overflow-hidden">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-neutral-white"
          onClick={() => toggleSection(section)}
        >
          <div className="flex items-center space-x-2">
            <Icon className="w-5 h-5" />
            <span className="font-medium">{title}</span>
            {isComplete && <Check className="w-4 h-4 text-green-500" />}
          </div>
          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
        
        {isExpanded && (
          <div className="p-4 border-t bg-white">
            {children}
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div className="flex items-center justify-center p-4">Loading...</div>;

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="mb-6">
        <h2 className="text-xl font-bold mb-2">Profile Completion</h2>
        <Progress value={getProgress()} className="w-full" />
        <p className="text-sm text-gray-600 mt-2">
          {getProgress()}% complete
        </p>
      </div>

      <div className="space-y-4">
        {renderSection("Certificates & Ratings", User, "certificates",
          <div id="certificates">
          <CertificatesSection 
            db={db} 
            onComplete={() => updateOnboardingStep('certificates', true)}
            />
          </div>
        )}

        {renderSection("Medical Certificate", Calendar, "medical",
          <div id="medical">
          <MedicalSection  
            db={db} 
            onComplete={() => updateOnboardingStep('medical', true)}
            />
          </div>
        )}

        {renderSection("Aircraft Information", Plane, "aircraft",
          <div id="aircraft">    
            <AircraftSection 
              db={db} 
              onComplete={() => updateOnboardingStep('aircraft', true)}
            />
          </div>
        )}

        {renderSection("Flight Experience", FileUp, "experience",
          <div id="experience">
            <ExperienceSection 
              db={db} 
              onComplete={() => updateOnboardingStep('experience', true)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingTracker;